import { useRef, useMemo } from "react"
import { any, arrayOf, object, shape, func, string } from "prop-types"
import { startCase } from "lodash"
import { Icon } from "source/shared/components"
import { GraduationYear } from "./GraduationYear"
import { useBuiltinFieldRules } from "source/shared/hooks/useBuiltinFieldRules"
import { useOrganizationGrades } from "source/shared/hooks/useOrganizationGrades"

const apiDataPropType = shape({
  attributes: object.isRequired,
  id: string.isRequired,
  type: string.isRequired,
})

GradeAndSchool.propTypes = {
  grade: any,
  isChild: any,
  school: any,
  schoolOptions: any,
  updateAttribute: any,
  updateAttributes: any,
  graduationYear: any,
}

export function GradeAndSchool({
  grade,
  isChild,
  school,
  schoolOptions,
  updateAttribute,
  updateAttributes,
  graduationYear = null,
}) {
  if (
    isChild ||
    !!school.attributes.school_id ||
    grade.attributes.grade !== null
  ) {
    return (
      <div className="d-f fd-c fd-r@sm g-2 f_1">
        <GradeOrGraduationYear
          {...{
            graduationYear,
            school,
            grade,
            updateAttribute,
          }}
        />
        <SchoolSelect
          additionalOptions={schoolOptions}
          grade={grade?.attributes?.grade || null}
          graduationYear={graduationYear?.attributes?.graduation_year || null}
          onChange={(id) => {
            const defaultSchoolAttributes = {
              beginning_grade: null,
              ending_grade: null,
            }
            const newSchoolAttributes =
              schoolOptions.find((option) => option.id === id)?.attributes ||
              defaultSchoolAttributes

            updateAttributes(school, {
              ...newSchoolAttributes,
              school_id: id,
            })
          }}
          value={school?.attributes.school_id}
        />
      </div>
    )
  } else return null
}

GradeOrGraduationYear.propTypes = {
  grade: any,
  graduationYear: any,
  school: any,
  updateAttribute: func,
}

function GradeOrGraduationYear({
  grade,
  graduationYear,
  school,
  updateAttribute,
}) {
  if (school?.attributes.school_id && !school?.attributes.ending_grade) {
    return <GraduationYear {...{ graduationYear, updateAttribute }} />
  }

  return (
    <GradeSelect
      grade={grade}
      onChange={(newGrade) => updateAttribute(grade, "grade", newGrade)}
      school={school}
      value={grade?.attributes?.grade?.toString() || ""}
    />
  )
}

GradeSelect.propTypes = {
  grade: any,
  onChange: func.isRequired,
  school: apiDataPropType,
  value: string.isRequired,
}

export function GradeSelect({
  value,
  onChange,
  school = {
    attributes: { beginning_grade: null, ending_grade: null },
  },
  grade,
}) {
  const organizationGrades = useOrganizationGrades()
  const { grade: gradeRules } = useBuiltinFieldRules()
  const initialGradePresent = useRef(grade?.attributes?.grade)

  const disabled =
    gradeRules === "view" ||
    (gradeRules === "add_if_blank" && initialGradePresent.current)

  const { beginning_grade = null, ending_grade = null } = school.attributes

  // Filter grades based on school grade range
  const filteredGrades = useMemo(() => {
    if (!organizationGrades?.data) return []

    return organizationGrades.data.filter((grade) => {
      const gradeKey = grade.attributes.key
      const meetsMinimum =
        beginning_grade === null || gradeKey >= beginning_grade
      const meetsMaximum = ending_grade === null || gradeKey <= ending_grade
      return meetsMinimum && meetsMaximum
    })
  }, [organizationGrades, beginning_grade, ending_grade])

  const grades = filteredGrades.map((grade) => {
    return (
      <option key={grade.attributes.key} value={grade.attributes.key}>
        {grade.attributes.value}
      </option>
    )
  })

  return (
    <div>
      <div className="label">
        Grade
        {disabled && <Icon symbol="general#lock" className="fs-2 pl-1" />}
      </div>
      <select
        aria-label="grade"
        className="select"
        disabled={disabled}
        id="grade"
        name="grade"
        onChange={(e) => onChange(e.target.value)}
        value={value}
      >
        <option key="No value" value="">
          Select...
        </option>
        {grades}
      </select>
    </div>
  )
}

SchoolSelect.propTypes = {
  additionalOptions: arrayOf(apiDataPropType),
  grade: string,
  graduationYear: string,
  onChange: func.isRequired,
  value: string.isRequired,
}

export function SchoolSelect({
  additionalOptions,
  grade,
  onChange,
  value,
  graduationYear = null,
}) {
  function buildOptionForSchool(option) {
    // Note: only an ending_range is required to create a range
    const { beginning_grade, ending_grade } = option.attributes

    const starting_grade = beginning_grade || -Infinity

    const gradeInRange =
      ending_grade &&
      Number(starting_grade) <= Number(grade) &&
      Number(ending_grade) >= Number(grade)

    const disableGradeSchool = grade !== null && !gradeInRange
    const disableNonGradeSchool = graduationYear && ending_grade

    const disableSchool =
      graduationYear === null ? disableGradeSchool : disableNonGradeSchool

    return (
      <option key={option.id} value={option.id} disabled={disableSchool}>
        {option.attributes.value}
      </option>
    )
  }

  const schoolGroups = groupSchoolsByType(additionalOptions)
  const schools = Object.keys(schoolGroups).map((type) => {
    const schoolGroup = schoolGroups[type]
    if (!schoolGroup.length) return null

    return (
      <optgroup key={type} label={startCase(type)}>
        {schoolGroup.map(buildOptionForSchool)}
      </optgroup>
    )
  })

  return (
    <div>
      <label htmlFor="school">School</label>
      <select
        className="select"
        id="school"
        name="school"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option key="0" value="">
          Select...
        </option>
        {schools}
      </select>
    </div>
  )
}

function groupSchoolsByType(schools) {
  const schoolGroups = {
    elementary: [],
    middle_school: [],
    high_school: [],
    college: [],
    other: [],
  }
  schools.forEach((option) => {
    const { school_types } = option.attributes
    school_types.forEach((type) => {
      if (!schoolGroups[type]) schoolGroups[type] = []
      schoolGroups[type].push(option)
    })
  })
  return schoolGroups
}
